import React from "react";
import { Button, Grid, Modal } from "semantic-ui-react";
import QuestionsBloc from "features/FolderManager/components/Questions/components/QuestionsBloc";
import { Formik } from "formik";
import { asyncForEach } from "utils/asyncForEach";
import * as Yup from "yup";
import { createYupSchema } from "utils/createYupSchema";
import isEqual from "lodash.isequal";

export default class Questions extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			validationSchema: null,
			initialValues: null,
			savedValues: null,
			formStatus: true,
			patientCommentOpen: false,
		};
	}

	getQuestions = (questions) => {
		const formatedQuestions = [];
		if (questions)
			questions.forEach((q) => {
				if (q.questions && q.questions.some((q) => !q.question.motivation))
					q.questions.forEach((qInfo) => {
						if (!qInfo.motivation) formatedQuestions.push(qInfo.question);
					});
			});
		return formatedQuestions;
	};

	getQuestionsWithErrors = (questions, errors) => {
		const questionsWithErrors = [];
		const idWithErrors = Object.keys(errors).map((key) => key);
		if (questions && idWithErrors.length > 0)
			questions.forEach((question) => {
				if (idWithErrors.includes(question.id.toString())) questionsWithErrors.push(question.label);
			});
		return questionsWithErrors;
	};

	getMotivations = (questions) => {
		const motivationQuestions = [];
		if (questions)
			questions.forEach((q) => {
				if (q.questions && q.questions.some((q) => q.question.motivation))
					q.questions.forEach((qInfo) => {
						if (qInfo.motivation) motivationQuestions.push(qInfo.question);
					});
			});
		return motivationQuestions;
	};

	getMotivationsWithErrors = (questions, errors) => {
		const motivationsWithErrors = [];
		const idWithErrors = Object.keys(errors).map((key) => key);
		if (questions && idWithErrors.length > 0)
			questions.forEach((question) => {
				if (idWithErrors.includes(question.id.toString())) motivationsWithErrors.push(question.label);
			});
		return motivationsWithErrors;
	};

	checkStatus = () => {
		if (this.props.formikRef) {
			this.props.formikRef
				.getFormikBag()
				.validateForm()
				.then((resValid) => {
					const formatedQuestions = [...this.getQuestions(this.props.data.systematic_questions), ...this.getQuestions(this.props.data.questions)];
					this.props.setQuestionErrors(this.getQuestionsWithErrors(formatedQuestions, resValid));
					const formatedMotivations = [...this.getMotivations(this.props.data.questions)];
					this.props.setMotivationsErrors(this.getMotivationsWithErrors(formatedMotivations, resValid));

					this.setState({
						formStatus: Object.entries(resValid).length === 0 && resValid.constructor === Object,
					});
					this.props.setFormStatus(this.state.formStatus);
				});
		}
	};

	componentDidMount() {
		this.initValues().then((res) => {
			this.setState({ initialValues: res, savedValues: res });
			this.checkStatus();
		});
	}

	componentDidUpdate(prevProps) {
		if (!isEqual(prevProps.data, this.props.data)) {
			this.initValues().then((res) => {
				this.setState({ initialValues: res });
				this.checkStatus();
			});
		}
	}

	componentWillUnmount() {
		this.props.setQuestionsDataToUpdate([]);
		this.setState({ initialValues: null, savedValues: null });
	}

	initValues = async () => {
		let { questions, systematic_questions } = this.props.data;
		let { mode } = this.props;
		if (this.props.setQuestionsDataToUpdate) this.props.setQuestionsDataToUpdate([]);
		let finalData = {};
		let validationSchemaBuild = [];
		await asyncForEach(systematic_questions, async (q) => {
			await asyncForEach(
				q.questions.filter((quest) => mode === "sample" || quest.question.target === "MIXTE"),
				async (i) => {
					if (i.question.adding_free_input !== null && i.question.adding_free_input !== "") {
						if (i.from_free_input) {
							// Pour les checkbox uniquement, au format particulier
							if (i.method === "checkbox") {
								let answer_options = await i.answer_options.map((ansOp) => ansOp.answer_option.label);
								// Obligé de récupérer de freeInput depuis les valeurs
								await asyncForEach(i.value, (v, index) => {
									if (v !== "freeInput" && answer_options.find((i) => i === v) === undefined) {
										// On set le freeInput avec la valeur du freeInput
										finalData = {
											...finalData,
											[i.question.id + "freeInput"]: v || "",
										};

										// On remet la valeur "freeInput" parmi les cases cochées du checkbox
										i.value.splice(index, 1, "freeInput");
									}
								});
							} else {
								if (i.value !== "freeInput") {
									finalData = {
										...finalData,
										[i.question.id + "freeInput"]: i.value || "",
									};
									i.value = "freeInput";
								}
							}
						} else {
							finalData = {
								...finalData,
								[i.question.id + "freeInput"]: "",
							};
						}
					}

					if (i.required) {
						if (i.method === "checkbox") {
							validationSchemaBuild.push({
								id: i.question.id,
								validationType: "array",
								validations: [
									{
										type: "min",
										params: ["1", "Réponse requise"],
									},
								],
							});
						} else if (i.method === "radio") {
							validationSchemaBuild.push({
								id: i.question.id,
								validationType: "string",
								validations: [
									{
										type: "min",
										params: ["1", "Réponse requise"],
									},
								],
							});
						} else
							validationSchemaBuild.push({
								id: i.question.id,
								validationType: "string",
								validations: [
									{
										type: "required",
										params: ["Réponse requise"],
									},
								],
							});
					}

					i.value = await this.formatFieldValueOnMethod(i);

					finalData = {
						...finalData,
						[i.question.id]: i.value || "",
					};
					let dataToUpdate = this.props.questionsDataToUpdate || [];
					dataToUpdate.push({
						id: i.question.id,
						method: i.question.method,
						links: i.links,
						value: i.value || "",
						from_free_input: i.from_free_input,
						has_free_input: i.question.adding_free_input !== null && i.question.adding_free_input !== "",
					});
					this.props.setQuestionsDataToUpdate(dataToUpdate);
				}
			);
		});

		await asyncForEach(questions, async (q) => {
			await asyncForEach(
				q.questions.filter((quest) => mode === "sample" || quest.question.target === "MIXTE"),
				async (i) => {
					if (i.question.adding_free_input !== null && i.question.adding_free_input !== "") {
						if (i.from_free_input) {
							// Pour les checkbox uniquement, au format particulier
							if (i.method === "checkbox") {
								let answer_options = await i.answer_options.map((ansOp) => ansOp.answer_option.label);
								// Obligé de récupérer de freeInput depuis les valeurs
								await asyncForEach(i.value, (v, index) => {
									if (v !== "freeInput" && answer_options.find((i) => i === v) === undefined) {
										// On set le freeInput avec la valeur du freeInput
										finalData = {
											...finalData,
											[i.question.id + "freeInput"]: finalData[i.question.id + "freeInput"] || v || "",
										};
										// On remet la valeur "freeInput" parmi les cases cochées du checkbox
										i.value.splice(index, 1, "freeInput");
									}
								});
							} else {
								if (i.method === "radio") {
									if (i.value !== "freeInput") {
										finalData = {
											...finalData,
											[i.question.id + "freeInput"]: i.value || "",
										};
										i.value = "freeInput";
										if (i.required) {
											validationSchemaBuild.push({
												id: i.question.id + "freeInput",
												validationType: "string",
												validations: [
													{
														type: "required",
														params: ["Réponse requise"],
													},
												],
											});
										}
									}
								} else if (i.value !== "freeInput") {
									finalData = {
										...finalData,
										[i.question.id + "freeInput"]: i.value || "",
									};
									i.value = "freeInput";
									if (i.required) {
										validationSchemaBuild.push({
											id: i.question.id + "freeInput",
											validationType: "string",
											validations: [
												{
													type: "min",
													params: ["1", "Réponse requise"],
												},
											],
										});
									}
								}
							}
						} else {
							finalData = {
								...finalData,
								[i.question.id + "freeInput"]: "",
							};
							if (i.required) {
								validationSchemaBuild.push({
									id: i.question.id + "freeInput",
									validationType: "string",
									validations: [
										{
											type: "min",
											params: ["1", "Réponse requise"],
										},
									],
								});
							}
						}
					}

					if (i.required) {
						if (i.method === "checkbox") {
							validationSchemaBuild.push({
								id: i.question.id,
								validationType: "array",
								validations: [
									{
										type: "min",
										params: ["1", "Réponse requise"],
									},
								],
							});
						} else {
							validationSchemaBuild.push({
								id: i.question.id,
								validationType: "string",
								validations: [
									{
										type: "min",
										params: ["1", "Réponse requise"],
									},
								],
							});
						}
					}

					i.value = await this.formatFieldValueOnMethod(i);

					finalData = {
						...finalData,
						[i.question.id]: i.value || "",
					};

					let dataToUpdate = this.props.questionsDataToUpdate;
					dataToUpdate.push({
						id: i.question.id,
						method: i.question.method,
						links: i.links,
						value: i.value || "",
						from_free_input: i.from_free_input,
						has_free_input: i.question.adding_free_input !== null && i.question.adding_free_input !== "",
					});
					this.props.setQuestionsDataToUpdate(dataToUpdate);
				}
			);
		});
		/** Generate schema validator Yup **/
		const yepSchema = validationSchemaBuild.reduce(createYupSchema, {});
		const validateSchema = Yup.object().shape(yepSchema);
		this.setState({
			validateSchema: validateSchema,
		});

		return finalData;
	};

	/**
	 *
	 * @param questionAnswer
	 * Format field value from json, depending on the input method
	 * @returns {*}
	 */
	formatFieldValueOnMethod = async (questionAnswer) => {
		if (questionAnswer.value === null) {
			if (questionAnswer.method === "checkbox") {
				return [];
			} else {
				return null;
			}
		} else {
			if (questionAnswer.method === "checkbox") {
				return questionAnswer.value;
			} else {
				if (Array.isArray(questionAnswer.value)) return questionAnswer.value[0];
				else {
					return questionAnswer.value;
				}
			}
		}
	};

	render() {
		let { patient, disabled, mode } = this.props;
		let { questions, systematic_questions } = this.props.data;
		const { initialValues, validateSchema, savedValues } = this.state;
		return (
			<div>
				<Formik
					enableReinitialize
					initialValues={initialValues}
					savedValues={savedValues}
					validationSchema={validateSchema}
					ref={this.props.setFormikRef}
					render={({ setFieldValue, setFieldTouched, values, errors, dirty, touched }) => {
						return (
							<fieldset
								disabled={disabled}
								onMouseLeave={() => {
									if (dirty) {
										this.props.onQuestionsSubmit(values, false);
										this.setState({ savedValues: values });
									}
								}}
							>
								<Grid style={{ margin: 0 }}>
									{values && Object.entries(values).length === 0 && values.constructor === Object && (
										<div style={{ marginTop: 20, marginBottom: 20 }}>Aucune question pour ce dossier.</div>
									)}

									{values &&
										Object.entries(values).length > 0 &&
										values.constructor === Object &&
										systematic_questions.filter(
											(quest) => mode === "sample" || (quest.questions && quest.questions.filter((q) => q.question.target === "MIXTE").length > 0)
										).length > 0 &&
										systematic_questions
											.filter(
												(quest) => mode === "sample" || (quest.questions && quest.questions.filter((q) => q.question.target === "MIXTE").length > 0)
											)
											.map((q, i) => (
												<Grid.Row columns={1} style={{ paddingTop: 0 }} key={"bloc_systematic_" + i}>
													<Grid.Column style={{ padding: 0 }}>
														<QuestionsBloc
															questions={q}
															setFieldValue={setFieldValue}
															setFieldTouched={setFieldTouched}
															values={values}
															errors={errors}
															touched={touched}
															prevalidFolders={() => {
																this.props.prevalidFolders();
																this.checkStatus();
															}}
															disabled={disabled}
														/>
													</Grid.Column>
												</Grid.Row>
											))}

									{values &&
										Object.entries(values).length > 0 &&
										values.constructor === Object &&
										questions.filter((quest) => mode === "sample" || quest.questions.filter((q) => q.question.target === "MIXTE").length > 0).length >
											0 &&
										questions
											.filter((quest) => mode === "sample" || quest.questions.filter((q) => q.question.target === "MIXTE").length > 0)
											.map((q, i) => (
												<Grid.Row columns={1} style={{ paddingTop: 0 }} key={"bloc" + i}>
													<Grid.Column style={{ padding: 0 }}>
														<QuestionsBloc
															questions={q}
															setFieldValue={setFieldValue}
															setFieldTouched={setFieldTouched}
															values={values}
															errors={errors}
															touched={touched}
															prevalidFolders={() => {
																this.props.prevalidFolders();
																this.checkStatus();
															}}
															disabled={disabled}
														/>
													</Grid.Column>
												</Grid.Row>
											))}
								</Grid>
							</fieldset>
						);
					}}
				/>
				<Modal
					open={this.state.patientCommentOpen}
					onClose={() =>
						this.setState({
							patientCommentOpen: false,
						})
					}
					size="small"
					closeOnDimmerClick
					closeOnEscape
				>
					<Modal.Header>Commentaire patient</Modal.Header>
					<Modal.Content>
						<div>{patient?.post_it}</div>
						{patient.callCenter_comment !== null && patient.callCenter_comment !== "" && (
							<div>Commentaire du CallCenter : {patient.callCenter_comment}</div>
						)}
					</Modal.Content>
					<Modal.Actions>
						<Button
							onClick={() =>
								this.setState({
									patientCommentOpen: false,
								})
							}
						>
							Fermer
						</Button>
					</Modal.Actions>
				</Modal>
			</div>
		);
	}
}
